import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SxProps,
  Theme,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../atomic/button';

type Props = {
  open: boolean;
  onConfirm?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
  title: string;
  contentText?: string;
  contentHtml?: string;
  confirmButtonText?: string;
  checkboxText?: string;
  sx?: SxProps<Theme>;
};

export const AgreementDialog: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  contentText,
  contentHtml,
  confirmButtonText,
  checkboxText
}) => {
  const { t } = useTranslation('common');
  const [scrollReachedEnd, setScrollReachedEnd] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // A document that fits without scrolling never fires a scroll event, so the
  // consent gate would stay closed forever.
  const measureContent = (el: HTMLDivElement | null) => {
    if (el && el.scrollHeight <= el.clientHeight + 15) {
      setScrollReachedEnd(true);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 15;
    setScrollReachedEnd(bottom);
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  return (
    <Dialog
      PaperProps={{
        className: 'text-center shadow-900 max-w-[70%] h-[75%]'
      }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          paddingTop: '24px',
          fontSize: '20px',
          lineHeight: 'normal',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '24px', width: '100%' }}>
        <div
          ref={measureContent}
          style={{
            overflowY: 'scroll',
            maxHeight: '70%',
            paddingLeft: '10px',
            paddingRight: '60px',
            textAlign: 'start'
          }}
          onScroll={handleScroll}
        >
          {/* pl-0 on the lists: preflight is disabled here, so the browser
              default list padding survives and would add to ml-6. */}
          <div
            className="[&_a]:break-words [&_a]:underline [&_h2]:mb-3 [&_h2]:mt-6 [&_h3]:mb-2 [&_h3]:mt-4 [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:ml-6 [&_ol]:pl-0 [&_ol]:list-decimal [&_p]:mb-3 [&_ul_ul]:list-[circle] [&_ul]:mb-3 [&_ul]:ml-6 [&_ul]:pl-0 [&_ul]:list-disc"
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{ __html: contentHtml ?? '' }}
          />
        </div>
        {!scrollReachedEnd && (
          <DialogContentText
            color="primary"
            sx={{
              whiteSpace: 'pre-line',
              paddingTop: '24px'
            }}
          >
            {contentText}
          </DialogContentText>
        )}
        <div style={{ marginBottom: scrollReachedEnd ? '3.5rem' : '0.5rem' }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              disabled={!scrollReachedEnd}
              onChange={handleCheckboxChange}
            />
          }
          label={checkboxText}
        />
        <div />
        <Button onClick={handleConfirm} disabled={!isChecked} className="mt-6 mx-auto">
          {confirmButtonText ?? t('CONFIRM')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
